import type { de } from './de';

/** English texts, with the same keys as the German ones. */
export const en: typeof de = {
  name: 'Fire safety officer',
  description:
    'Inspections of buildings with findings, photos, attachments and report, also without a connection.',
  nav: { inspections: 'Inspections' },
  entities: { inspection: 'Inspection' },
  permissions: {
    inspection: {
      read: {
        label: 'View inspections',
        description:
          'Open inspections with their findings, photos and attachments and create reports. With the scope “Own” only inspections the person inspects or created.',
      },
      write: {
        label: 'Edit inspections',
        description:
          'Create and change inspections, record findings and photos, upload attachments. With the scope “Own” only own inspections.',
      },
      delete: {
        label: 'Delete inspections',
        description: 'Delete inspections with their findings, photos and attachments.',
      },
      comment: {
        label: 'Comment on inspections',
        description: 'Write comments on inspections.',
      },
      export: {
        label: 'Export inspections',
        description: 'Download the list of inspections as a CSV file.',
      },
    },
    settings: {
      manage: {
        label: 'Set up fire safety',
        description:
          'Maintain the defect catalogue, categories, report templates and questionnaires of the company and change the settings of the module.',
      },
    },
  },
  roles: {
    inspector: 'Fire safety officer: Carry out inspections',
    manager: 'Fire safety officer: Manage inspections',
  },
  settings: {
    offlineDays: {
      label: 'Completed inspections offline (days)',
      description:
        'How long after “Report sent” inspections stay on the devices of their inspectors. With 0 only open inspections.',
    },
  },
  offline: { inspections: 'Inspections' },
  documents: {
    inspectionReport: 'Inspection report',
    groups: {
      object: 'Object',
      customer: 'Customer',
      inspection: 'Inspection',
      findings: 'Findings',
    },
    placeholders: {
      object: {
        name: 'Name',
        number: 'Object number',
        customerNumber: 'Customer’s object number',
        address: 'Address',
        street: 'Street and house number',
        postcode: 'Postcode',
        city: 'City',
        state: 'Federal state',
        usage: 'Use',
        buildingClass: 'Building class',
      },
      customer: {
        name: 'Name',
        number: 'Customer number',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        website: 'Website',
        contact: 'Contact person',
      },
      inspection: {
        title: 'Title',
        date: 'Date',
        inspectors: 'Inspectors',
        participants: 'Further participants',
        attendees: 'People present',
        status: 'Status',
        remarks: 'Remarks',
      },
      findings: { count: 'Total number', open: 'Number open' },
    },
    sources: { attendees: 'People present', remarks: 'Remarks' },
  },
  ai: { findingDraft: 'Draft a defect from notes' },
  questionnaires: { general: 'General information' },
  statuses: {
    planned: 'Planned',
    'in-progress': 'In progress',
    completed: 'Report sent',
  },
  audit: {
    inspection: {
      create: 'Inspection created',
      update: 'Inspection changed',
      start: 'Inspection started',
      complete: 'Report sent',
      reopen: 'Inspection reopened',
      delete: 'Inspection deleted',
    },
  },
  guides: {
    inspections: {
      title: 'Inspections',
      summary: 'Plan, carry out and complete inspections, and who may do what.',
    },
  },
  errors: {
    object: 'Please choose an object that is available in Projects and not archived.',
    project: 'Please choose a project of the company that you may see and that is not archived.',
    projectObject: 'The object does not belong to this project.',
    title: 'Please enter a title (at most 200 characters).',
    date: 'Please enter a valid date.',
    inspector: 'Please choose active accounts of the company as inspectors.',
    inspectors: 'At most 20 inspectors, each person once.',
    participants: 'Further participants may be at most 1000 characters long.',
    attendee: 'Every person present needs a name. Name and function: at most 200 characters.',
    attendees: 'At most 100 people present.',
    remarks: 'The remarks may be at most 20,000 characters long.',
    attachment:
      'Attachments are PDF files of this inspection, each with a title (at most 200 characters).',
    attachments: 'At most 50 attachments, each file once.',
    completedAt: 'Please enter a time that is not in the future.',
    status: 'This is not possible in the current status of the inspection. Please reload the page.',
    completed: 'The report has been sent. Reopen the inspection to record defects.',
  },
  report: {
    title: 'Inspection report {{object}}',
    inspector: 'Inspector {{number}}',
    attendeeName: 'Name',
    attendeeRole: 'Function',
    checks: {
      questionnaire: 'General information: {{open}} questions without an answer.',
      attendees: 'No attendees are entered.',
      remarks: 'No remarks are entered.',
    },
    sample: {
      remarks: 'Second escape route on the upper floor discussed on site with the caretaker.',
    },
  },
  sections: { inspections: 'Fire safety inspections' },
  pages: {
    unavailable: 'not available',
    offlinePerson: 'Name only with a connection',
    errors: {
      versionConflict:
        'The inspection was changed in the meantime. The page now shows the new state; please repeat your change.',
      invalidInput: 'Please check your entries.',
      notFound: 'The inspection no longer exists.',
      forbidden: 'You are not allowed to do this.',
    },
    list: {
      title: 'Inspections',
      description: 'Fire safety inspections at the objects of the company, newest first.',
      search: 'Search inspections',
      searchPlaceholder: 'Title, object or project',
      exportName: 'Fire safety inspections',
      offline: 'No connection: inspections on this device',
      allStatuses: 'All statuses',
      allObjects: 'All objects',
      allProjects: 'All projects',
      allInspectors: 'All inspectors',
      from: 'Date from',
      to: 'Date to',
      noMatches: 'No matching inspections',
      noMatchesHint: 'Change the search or the filters.',
      empty: 'No inspections yet',
      emptyHint: 'Create the first inspection with "Start inspection".',
      emptyReadOnly: 'Inspections show here once they are created.',
      findings: '{{open}} / {{total}}',
      columns: {
        title: 'Title',
        status: 'Status',
        object: 'Object',
        project: 'Project',
        date: 'Date',
        inspectors: 'Inspectors',
        findings: 'Defects open / total',
      },
    },
    section: {
      date: 'Date',
      title: 'Title',
      object: 'Object',
      status: 'Status',
      openFindings: 'Open defects',
      empty: 'No fire safety inspections yet',
      emptyHint: 'Create the first one here with “Start inspection”.',
      emptyReadOnly: 'Fire safety inspections show here once they are created.',
    },
    start: {
      action: 'Start inspection',
      title: 'Start inspection',
      planTitle: 'Plan inspection',
      description: 'With a date in the future the inspection is planned.',
      submit: 'Start',
      plan: 'Plan',
      started: 'Inspection started',
      planned: 'Inspection planned',
      copyFailed: 'The inspection was created, but the defects could not be taken over.',
      offlineUnavailable:
        'Without a connection inspections can only be created when the device keeps fire safety inspections offline.',
      object: 'Object',
      objectPlaceholder: 'Choose an object on this device',
      project: 'Project',
      objectHint: 'Project and customer follow from the object.',
      projectObjectHint: 'One of the objects of the project.',
      projectObjectPlaceholder: 'Choose an object of the project',
      noObjects: 'The project has no objects',
      noObjectsHint:
        'An inspection takes place at an object. Add an object to the project under “Objects”, then the inspection can start.',
      projectClosed: 'Project not open',
      projectClosedHint:
        'Inspections start only in open projects of your own company, not in closed, cancelled or archived ones.',
      projectLoading: 'Finding the project',
      noProject: 'No open project at this object',
      noProjectHint:
        'An inspection always belongs to a project. Add the object to a running project under Projects, then the inspection can start.',
      projectChoice: 'Several open projects cover the object. Which one is the inspection part of?',
      projectPlaceholder: 'Choose a project',
      projectRequired: 'Please choose the project of the inspection.',
      otherProject: 'Choose another project',
      customer: 'Customer',
      noCustomer: 'The project has no client.',
      titleField: 'Title',
      titleHint: 'Without one "Begehung" with the date.',
      date: 'Date',
      dateHint: 'A date in the future plans the inspection.',
      template: 'General information',
      templatesUnavailable:
        'The templates of the general information are not on this device. The inspection starts without a questionnaire.',
      copy: 'Take over defects from an earlier inspection',
      copyFrom: 'Earlier inspection',
      copyNone: 'Take over none',
      closedFinding: 'done',
      positiveFinding: 'positive',
      copyOffline: 'Defects can only be taken over with a connection.',
    },
    detail: {
      notFound: 'Inspection not found',
      notFoundHint: 'It was deleted, or you lack the permission.',
      notOnDevice: 'Inspection not on this device',
      notOnDeviceHint:
        'Without a connection the app opens only inspections on the device. Open it once with a connection first.',
      onlineOnly: 'Not available without a connection',
      onlineOnlyHint:
        'Your device keeps nothing offline in this company, for example because you work as a superadmin in a company other than your own.',
      report: 'Create report',
      steps: {
        start: 'Start',
        complete: 'Report sent',
        reopen: 'Reopen',
        startDone: 'Inspection started',
        completeDone: 'Marked as sent',
        reopenDone: 'Inspection reopened',
      },
      deleted: '"{{title}}" deleted',
      offlineRefused: 'A change on this device was refused',
      offlineRefusedHint:
        'Reason: {{reason}}. Details and discarding in the "Sync" window in the top bar.',
      completedTitle: 'Report sent',
      completedSince:
        'Since {{when}}. Information, findings and attachments change only after "Reopen"; comments still work.',
      objectPlans: 'Plans of the object',
      projectPlans: 'Plans of the project',
      noFinding: 'According to the AI probably no defect: {{reason}}',
      noPositive: 'In the view of the AI this is nothing positive: {{reason}}',
      plansHint: 'Plans of the object and the project. Findings can be placed on them.',
      deleteTitle: 'Delete inspection?',
      deleteDescription: '"{{title}}" will be deleted.',
      deleteHeavy:
        '"{{title}}" will be deleted with {{findings}} findings, {{files}} files and the comments. This cannot be undone.',
      deleteWord: 'DELETE',
      deleteWordLabel: 'Type DELETE to confirm',
      questionnaire: 'General information',
      noQuestionnaire: 'No general information.',
      attendees: 'Attendees',
      attendeeName: 'Name',
      attendeeRole: 'Function',
      addAttendee: 'Add person',
      attendeeRow: 'Attendee {{number}}',
      removeAttendee: 'Remove attendee {{number}}',
      noAttendees: 'No attendees yet.',
      none: 'Not given.',
      notes: 'Participants and remarks',
      participants: 'Further participants',
      participantsHint: 'E.g. representatives of the client or the fire brigade.',
      remarks: 'Remarks',
      reportAttachments: 'Attachments of the report',
      reportAttachmentsHint: 'PDF files of the inspection that follow the report, in this order.',
      noReportAttachments: 'No attachments in the report yet.',
      moveUp: 'Move "{{name}}" up',
      moveDown: 'Move "{{name}}" down',
      removeFromReport: 'Take "{{name}}" out of the report',
      addToReport: 'Attach "{{name}}"',
      editTitle: 'Edit inspection',
      inspectors: 'Inspectors',
      inspectorsOffline: 'Inspectors can only be changed with a connection.',
      sections: 'Sections of the inspection',
      tabs: {
        findings: 'Defects',
        questionnaire: 'Questionnaire',
        protocol: 'Minutes',
        plans: 'Plans',
        files: 'Files',
        history: 'History',
      },
      openFindings: '{{count}} open',
      meta: {
        date: 'Date',
        object: 'Object',
        project: 'Project',
        customer: 'Customer',
        inspectors: 'Inspectors',
      },
    },
  },
};
